import { use, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { DateString, todayDate } from '../../../../common/src/date_string.ts';
import type { SongList } from '../../../../common/src/model.ts';
import {
  DailySingingPlan,
  hymnOfTheMonthCategory,
  SingingPlanEntry,
  SongListType,
  SongSlug,
  WeeklySingingPlan,
} from '../../../../common/src/model.ts';
import { getHymnOfTheMonthOrTermTitle } from '../../../../common/src/org.ts';
import { Pages } from '../../../../common/src/pages.ts';
import { decideSongClickGate } from '../../../../common/src/subscription_banner_policy.ts';
import { getHour } from '../../../../common/src/time_string.ts';
import BackArrowIcon from '../../assets/arrow-previous-left-icon.svg?react';
import StopwatchIcon from '../../assets/stopwatch.svg?react';
import { useCurrentOrg } from '../../data/use_current_org.tsx';
import { useCustomMusic } from '../../data/use_custom_music.tsx';
import { useHousehold } from '../../data/use_household.tsx';
import { HymnalsContext } from '../../data/use_hymnals.tsx';
import { allSongsCompletedToday, useSingingPlanCompletions } from '../../data/use_singing_plan_completions.ts';
import { useUserAttributes } from '../../data/use_user_attributes.ts';
import { SequencerContext } from '../../player/sequencer.tsx';
import { isOptimisticallySubscribed } from '../../util/optimistic_subscription.ts';
import { getHymnFromSlug, getUrlForPage } from '../../util/path.ts';
import { ContactSupportLink, Pencil } from '../../util/shared.tsx';
import { Spinner } from '../../util/spinner.tsx';
import { storeProspectiveSubscriptionOrigin } from '../../util/subscription_flow_origin.ts';
import { useCurrentDate } from '../../util/use_current_date.tsx';
import { useCurrentTime } from '../../util/use_current_time.tsx';
import { CheckMarkButton, Divider, Tile } from './shared.tsx';

export const SingingPlanTile = ({
  title,
  singingPlan,
  currentDate,
  setIsStreakVisible,
}: {
  title: string;
  singingPlan?: WeeklySingingPlan;
  currentDate: DateString;
  setIsStreakVisible: (value: boolean) => void;
}) => {
  const dailyPlan = singingPlan?.days.find(({ date }) => date === currentDate);

  const onBackFromDay = useCallback(() => {
    setIsStreakVisible(true);
  }, [setIsStreakVisible]);

  return <DailySongList title={title} dailyPlan={dailyPlan} date={currentDate} onBack={onBackFromDay} />;
};

const DailySongList = ({
  title,
  dailyPlan,
  date,
  onBack,
}: {
  title: string;
  dailyPlan?: DailySingingPlan;
  date: DateString;
  onBack: () => void;
}) => {
  const { customMusic } = useCustomMusic();
  const hymnals = use(HymnalsContext);
  const { isSongCompleted, markSongCompleted } = useSingingPlanCompletions();
  const { household } = useHousehold();
  const { currentOrg, householdOrgs } = useCurrentOrg();
  const { openPlayer } = use(SequencerContext);
  const { isInternalUser } = useUserAttributes();
  const navigate = useNavigate();
  const today = useCurrentDate();
  const songClickGateDecision = decideSongClickGate({
    page: Pages.Today,
    organization: currentOrg,
    household,
    clientDate: today,
    isBypassUser: isInternalUser,
    isOptimisticallySubscribed: isOptimisticallySubscribed({ household }),
  });

  const onCompleteSong = useCallback(
    (songSlug: SongSlug): (() => void) =>
      () => {
        markSongCompleted(date, songSlug);
        if (allSongsCompletedToday(household, householdOrgs, today)) {
          onBack();
        }
      },
    [date, household, householdOrgs, markSongCompleted, onBack, today],
  );

  const onOpenSong = useCallback(
    ({ songSlug, songTitle }: { songSlug: SongSlug; songTitle?: string }): (() => void) =>
      () => {
        if (!hymnals || !customMusic) {
          return;
        }
        const playlist: SongList = {
          type: SongListType.Playlist,
          date: todayDate(),
          songs: [{ row: 1, slug: songSlug }],
        };
        if (songClickGateDecision.variant === 'gate') {
          storeProspectiveSubscriptionOrigin({ originPage: Pages.Today, playlist, playlistIndex: 0 });
          void navigate(getUrlForPage(Pages.SubscriptionTrial), { state: { songTitle } });
          return;
        }
        openPlayer({ playlist });
      },
    [customMusic, hymnals, navigate, openPlayer, songClickGateDecision.variant],
  );

  const translateSingingPlanCategory = useCallback(
    (song: SingingPlanEntry) => {
      const { category } = song;
      if (category !== hymnOfTheMonthCategory) {
        return category;
      }
      const hymn = getHymnFromSlug({ songSlug: song.slug, hymnals, customMusic });
      if (!hymn) {
        return category;
      }
      return getHymnOfTheMonthOrTermTitle([hymn], 'Month');
    },
    [customMusic, hymnals],
  );

  const onEditSeasonalSongList = useCallback(async () => {
    await navigate(getUrlForPage(Pages.SeasonalCalendar));
  }, [navigate]);

  const isBackButtonVisible = date !== today || allSongsCompletedToday(household, householdOrgs, today);
  const currentHour = getHour(useCurrentTime());
  const hoursLeft = !isBackButtonVisible && 24 - currentHour;
  return (
    <Tile>
      <TileHeader key="tile-header">
        <HeadingGroup>
          {isBackButtonVisible && <BackButton onClick={onBack} />}
          <TileHeadingText>{title}</TileHeadingText>
          {isInternalUser && <Pencil onClick={onEditSeasonalSongList} />}
        </HeadingGroup>
        {hoursLeft && (
          <TimeLeftWrapper>
            <Stopwatch />
            <TimeLeft>{hoursLeft} HOURS</TimeLeft>
          </TimeLeftWrapper>
        )}
      </TileHeader>
      {!dailyPlan ? (
        <NoSingingPlan>
          Check back later to see a singing plan for your household or <ContactSupportLink /> if you still don&apos;t
          have a singing plan.
        </NoSingingPlan>
      ) : (
        <SongList key="song-list">
          {hymnals.length === 0 ? (
            <Spinner />
          ) : (
            dailyPlan.songs
              .map((song) => ({
                ...song,
                category: translateSingingPlanCategory(song),
                ...getHymnFromSlug({ songSlug: song.slug, hymnals, customMusic }),
              }))
              .map((song) => ({
                ...song,
                number: song.songNumber,
              }))
              .map((song) => (
                <Song key={song.category + song.slug}>
                  <CheckMarkButton
                    checked={isSongCompleted(date, song.slug)}
                    onClick={onCompleteSong(song.slug)}
                    isPurple={true}
                  />
                  <SongInfo
                    id={getTodaySongElementId(song.slug)}
                    onClick={onOpenSong({ songSlug: song.slug, songTitle: song.title })}
                    $isDisabled={songClickGateDecision.variant === 'gate'}
                  >
                    <Category>{song.category}</Category>
                    <Divider />
                    <SongName>
                      {song.number ? `${song.number} - ` : ''}
                      {song.title}
                      {song.psalm ? ` - ${song.psalm}` : ''}
                    </SongName>
                  </SongInfo>
                </Song>
              ))
          )}
        </SongList>
      )}
    </Tile>
  );
};

export function getTodaySongElementId(songSlug: SongSlug): string {
  return `today-song-${songSlug}`;
}

const BackButton = styled(BackArrowIcon)`
  width: 12px;
  cursor: pointer;
  fill: var(--color-text);
`;

const TileHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: Jost-SemiBold, Arial, sans-serif;
`;

const HeadingGroup = styled.div`
  display: flex;
  gap: 15px;
  color: var(--color-section-heading); /* also colors the pencil via currentColor */
`;

const TileHeadingText = styled.div`
  font-size: 1.6rem;
  line-height: 1.6rem;
`;

const TimeLeftWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 5px;
`;

const Stopwatch = styled(StopwatchIcon)`
  fill: var(--color-text-singing-plan-callout);
  width: 20px;
`;

const TimeLeft = styled.div`
  color: var(--color-text-singing-plan-callout);
  text-wrap: nowrap;
`;

const SongList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Song = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;
`;

const SongInfo = styled.div<{ $isDisabled: boolean }>`
  display: flex;
  flex-direction: column;
  cursor: pointer;
  color: ${(props) => (props.$isDisabled ? 'var(--color-text-disabled)' : 'var(--color-text)')};
`;

const Category = styled.div`
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 1.4rem;
`;

const SongName = styled.div`
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 0.9rem;
`;

const NoSingingPlan = styled.div``;
